import React, { PropsWithChildren, useMemo } from "react";

import { Box, Stack } from "@mui/material";
import { format, set } from "date-fns";
import { useFlags } from "launchdarkly-react-client-sdk";

import useCheckPermission from "@fartherfinance/frontend/api/Authz/hooks/useCheckPermission";
import useGetPortfolioBillingVersions from "@fartherfinance/frontend/api/Investment/hooks/useGetPortfolioBillingVersions";

import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import Conditional from "@src/sharedComponents/Conditional/Conditional";
import LinkButton from "@src/sharedComponents/LinkButton/LinkButton";
import Text from "@src/sharedComponents/Typography/Text";

import EquivalentSecurities from "./EquivalentSecurities/AdminTable/EquivalentSecurities";
import { useMonthList } from "./utils/useMonthList";

import styles from "./AdminPage.module.css";

const AdminPage = ({ children }: PropsWithChildren<unknown>): JSX.Element => {
  const auth = useAdvisorRequestAuth();

  const {
    enableAdminCreateCxAdvisor,
    enableAdminBulkAccountUpload,
    enableAdminFamBilling,
    enableAdminCustomModelBilling,
    enableCreateAdvisorV2,
    enableEquivalentSecuritiesAdmin,
  } = useFlags();

  const monthList = useMonthList();

  const canCreateCX = useCheckPermission(
    {
      type: "Organization",
      action: "create.ops",
      id: "Farther",
    },
    auth
  );

  const canBulkCreateClient = useCheckPermission(
    {
      type: "Organization",
      action: "create.client.bulk",
      id: "Farther",
    },
    auth
  );

  const canGenerateBilling = useCheckPermission(
    {
      type: "TradingGroup",
      action: "billing.generate",
      // We are looking for a generic permission for the TradingGroup resource
      // and not a specific instance
      id: null,
    },
    auth
  );

  const { data: billingData } = useGetPortfolioBillingVersions(
    {
      page: 1,
      pageSize: 100,
    },
    auth
  );

  const allPorfoliosBillingPeriod = useMemo(() => {
    if (!billingData?.data || billingData?.data.length === 0) {
      return undefined;
    }

    const earliest = billingData?.data.reduce((currentEarliest, candidate) => {
      const isEarlierYear = candidate.billingYear < currentEarliest.billingYear;

      const isSameYearEarlierMonth =
        candidate.billingYear === currentEarliest.billingYear &&
        candidate.billingMonth < currentEarliest.billingMonth;

      if (isEarlierYear || isSameYearEarlierMonth) {
        return candidate;
      }

      return currentEarliest;
    });

    const date = set(new Date(), {
      year: earliest.billingYear,
      month: earliest.billingMonth - 1,
      date: 1,
    });

    return format(date, "MMM yyyy");
  }, [billingData?.data]);

  const otherBillingDateRange = useMemo(() => {
    if (!monthList || monthList.length === 0) {
      return undefined;
    }

    const first = monthList[monthList.length - 1].label;
    const last = monthList[0].label;

    return `${first} - ${last}`;
  }, [monthList]);

  const showOtherBillingDateRange = useMemo(() => {
    return [enableAdminFamBilling, enableAdminCustomModelBilling].some(
      (flag) => flag === true
    );
  }, [enableAdminFamBilling, enableAdminCustomModelBilling]);

  return (
    <Stack width={640} margin={"auto"} marginTop={"40px"} gap={"24px"}>
      <Conditional
        condition={
          enableAdminCreateCxAdvisor && canCreateCX.data?.authorized === true
        }
      >
        <Box className={styles.section}>
          <Text className={styles.title} variant="title3">
            CX Team
          </Text>
          <Box className={styles.row}>
            <Text variant="body">Create CX Team Member</Text>

            <LinkButton
              variant={"outlined"}
              buttonType={"secondary"}
              text={"Create CX Member"}
              to={"/Advisor/Admin/CreateCXMember"}
              sx={{ height: "32px" }}
            />
          </Box>
        </Box>
      </Conditional>

      <Conditional
        condition={
          enableAdminBulkAccountUpload &&
          canBulkCreateClient.data?.authorized === true
        }
      >
        <Box className={styles.section}>
          <Text className={styles.title} variant="title3">
            Bulk Create Client Accounts
          </Text>
          <Box className={styles.row}>
            <Text variant="body">
              Create multiple client accounts at once from a spreadsheet
            </Text>

            <LinkButton
              variant={"outlined"}
              buttonType={"secondary"}
              text={"Upload Clients CSV"}
              to={"/Advisor/Admin/CreateBulkClients"}
              sx={{ height: "32px" }}
            />
          </Box>
        </Box>
      </Conditional>

      <Conditional condition={enableCreateAdvisorV2}>
        <Box className={styles.section}>
          <Text className={styles.title} variant="title3">
            Advisor Team
          </Text>
          <Box className={styles.row}>
            <Text variant="body">Create an Advisor Team Member</Text>

            <LinkButton
              variant={"outlined"}
              buttonType={"secondary"}
              text={"Create New Advisor Member"}
              to={"/Advisor/Admin/CreateAdvisor"}
              sx={{ height: "32px" }}
            />
          </Box>
        </Box>
      </Conditional>

      <Conditional condition={canGenerateBilling.data?.authorized === true}>
        <Box className={styles.section}>
          <Text className={styles.title} variant="title3">
            Billing
            <Text variant="bodySmall" color="textSubtle">
              Download a CSV to process billing for model portfolios.
            </Text>
          </Text>
          <Box className={styles.labeledInfoRow}>
            <Text
              className={styles.space}
              variant="bodySmall"
              color="textSubtle"
            >
              Billing Period
            </Text>
            <Text variant="bodySmall">
              {allPorfoliosBillingPeriod} - Current Month
            </Text>
          </Box>
          <Conditional condition={enableAdminFamBilling}>
            <Box className={styles.row}>
              <Text variant="body">FAM Billing</Text>
              <LinkButton
                variant={"outlined"}
                buttonType={"secondary"}
                text={"Export"}
                to={"/Advisor/Admin/AllPortfoliosBilling?reportType=fam"}
                sx={{ height: "32px" }}
              />
            </Box>
            <Box className={styles.row}>
              <Text variant="body">Non-Fam billing</Text>
              <LinkButton
                variant={"outlined"}
                buttonType={"secondary"}
                text={"Export"}
                to={"/Advisor/Admin/AllPortfoliosBilling?reportType=non-fam"}
                sx={{ height: "32px" }}
              />
            </Box>
          </Conditional>
          <Conditional condition={showOtherBillingDateRange}>
            <Box className={styles.labeledInfoRow}>
              <Text
                className={styles.space}
                variant="bodySmall"
                color="textSubtle"
              >
                Billing Period
              </Text>
              <Text variant="bodySmall">{otherBillingDateRange}</Text>
            </Box>
          </Conditional>
          <Conditional condition={enableAdminFamBilling}>
            <Box className={styles.row}>
              <Text variant="body">FAM Billing</Text>
              <LinkButton
                variant={"outlined"}
                buttonType={"secondary"}
                text={"Export"}
                to={"/Advisor/Admin/FAMBilling"}
                sx={{ height: "32px" }}
              />
            </Box>
          </Conditional>
          <Conditional condition={enableAdminCustomModelBilling}>
            <Box className={styles.row}>
              <Text variant="body">Custom Model Billing</Text>
              <LinkButton
                variant={"outlined"}
                buttonType={"secondary"}
                text={"Export"}
                to={"/Advisor/Admin/CustomModelBilling"}
                sx={{ height: "32px" }}
              />
            </Box>
          </Conditional>
        </Box>
      </Conditional>

      <Conditional condition={enableEquivalentSecuritiesAdmin}>
        <EquivalentSecurities />
      </Conditional>

      <Box className={styles.section}>
        <Text className={styles.title} variant="title3">
          Add Disclosure
        </Text>
        <Box className={styles.row}>
          <Text variant="body">Upload a new disclosure agreement</Text>
          <LinkButton
            variant={"outlined"}
            buttonType={"secondary"}
            text={"Upload Document"}
            to={"/Advisor/Admin/Disclosures"}
            sx={{ height: "32px" }}
          />
        </Box>
      </Box>

      {children}
    </Stack>
  );
};

export default AdminPage;
